import React from "react";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Search, ExternalLink, Sparkles, Loader2, TrendingUp, BarChart3, Lightbulb } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { normalizeError } from '@/lib/normalizeError';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

interface TrendEvent {
  id: string;
  title: string;
  summary: string | null;
  url: string | null;
  tags: string[];
  score: number;
  occurred_at: string;
  trend_sources: {
    name: string;
  };
}

interface GeneratedIdea {
  title: string;
  problem: string;
  solution: string;
  icp: string;
  hooks: string[];
  plan: string[];
  score: number;
}

export default function TrendBrowser() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [trends, setTrends] = useState<TrendEvent[]>([]);
  const [filteredTrends, setFilteredTrends] = useState<TrendEvent[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [topTrendsMode, setTopTrendsMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [previewIdea, setPreviewIdea] = useState<GeneratedIdea | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [totalIdeasGenerated, setTotalIdeasGenerated] = useState(0);
  const { toast } = useToast();

  // Clean HTML entities from text using DOMParser for safety
  const cleanText = (text: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    return doc.body.textContent || '';
  };

  // Fetch trends
  const fetchTrends = async () => {
    try {
      const { data, error } = await supabase
        .from('trend_events_legacy')
        .select(`
          id,
          title,
          summary,
          url,
          tags,
          score,
          occurred_at,
          trend_sources!inner (
            name
          )
        `)
        .order('occurred_at', { ascending: false })
        .limit(200);

      if (error) throw error;
      
      const typedData = (data || []).map(item => ({
        ...item,
        tags: Array.isArray(item.tags) ? item.tags as string[] : []
      })) as TrendEvent[];
      
      setTrends(typedData);
      setFilteredTrends(typedData);
    } catch (error) {
      console.error('Error fetching trends:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch trends',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch total ideas count
  const fetchIdeasCount = async () => {
    const { count } = await supabase
      .from('ideas')
      .select('*', { count: 'exact', head: true });
    setTotalIdeasGenerated(count || 0);
  };

  // Set up real-time subscription
  useEffect(() => {
    fetchTrends();
    fetchIdeasCount();

    const channel = supabase
      .channel('trend_events_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'trend_events_legacy',
        },
        () => {
          fetchTrends();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // Filter and sort trends
  useEffect(() => {
    let filtered = trends;

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (trend) =>
          trend.title.toLowerCase().includes(query) ||
          trend.summary?.toLowerCase().includes(query)
      );
    }

    if (sourceFilter) {
      filtered = filtered.filter(
        (trend) => trend.trend_sources.name === sourceFilter
      );
    }

    // Sort by score if top trends mode is on
    if (topTrendsMode) {
      filtered = [...filtered].sort((a, b) => b.score - a.score);
    }

    setFilteredTrends(filtered);
  }, [searchQuery, sourceFilter, trends, topTrendsMode]);

  // Generate idea from trend
  const handleGenerateIdea = async (trend: TrendEvent) => {
    // Check if user is authenticated
    if (!user) {
      toast({
        title: 'Sign In Required',
        description: 'Please sign in to generate startup ideas from trends.',
        variant: 'destructive',
      });
      navigate('/auth');
      return;
    }

    setGeneratingId(trend.id);
    
    try {
      console.log('Starting idea generation for trend:', trend.id);
      
      const { data, error } = await supabase.functions.invoke('idea', {
        body: {
          niche: `${trend.trend_sources.name} — ${trend.title}`,
          constraints: trend.summary 
            ? `Based on this trend: ${cleanText(trend.summary).substring(0, 500)}` 
            : undefined,
        },
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        
        // Check if it's a rate limit error
        if (error.message?.includes('rate_limit_exceeded') || error.message?.includes('daily limit')) {
          throw new Error('You\'ve reached your daily limit of 15 idea generations. Please try again tomorrow! 🌅');
        }
        
        throw new Error(error.message || 'Failed to generate idea');
      }

      if (!data || !data.idea) {
        // Check if data has error property (from edge function response)
        if (data?.error === 'rate_limit_exceeded') {
          throw new Error(data.message || 'You\'ve reached your daily limit of idea generations. Please try again tomorrow! 🌅');
        }
        throw new Error('Invalid response from idea generator');
      }

      setPreviewIdea(data.idea);
      setShowPreviewModal(true);
      fetchIdeasCount();
      
      toast({
        title: '✨ Idea Generated!',
        description: `Created "${data.idea.title}" from trend.`,
      });
    } catch (err) {
      const e = normalizeError(err);
      console.error('Error generating idea:', e.name, e.message, e.raw);
      toast({
        title: 'Generation Failed',
        description: e.message + '. Please try again or contact support if this persists.',
        variant: 'destructive',
      });
    } finally {
      setGeneratingId(null);
    }
  };

  const handleSaveIdea = () => {
    setShowPreviewModal(false);
    toast({
      title: 'Idea Saved!',
      description: `"${previewIdea?.title}" has been saved to your ideas.`,
    });
  };

  // Sync trends from all sources
  const handleSyncTrends = async () => {
    if (syncing) return;
    
    setSyncing(true);
    try {
      toast({
        title: 'Syncing Trends...',
        description: 'Fetching latest trends from HackerNews, Product Hunt, and Reddit.',
      });

      const { data, error } = await supabase.functions.invoke('sync-all-trends');
      
      if (error) throw error;

      const summary = data?.summary;
      toast({
        title: 'Sync Complete!',
        description: `Fetched ${summary?.totalTrends || 0} trends from ${summary?.successfulSources || 0} sources.`,
      });

      // Refresh trends list
      fetchTrends();
    } catch (err) {
      const e = normalizeError(err);
      console.error('Sync error:', e.name, e.message, e.raw);
      toast({
        title: 'Sync Failed',
        description: e.message,
        variant: 'destructive',
      });
    } finally {
      setSyncing(false);
    }
  };

  // Get unique sources
  const sources = Array.from(new Set(trends.map((t) => t.trend_sources.name)));

  // Calculate analytics
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const trendsToday = trends.filter(
    (t) => new Date(t.occurred_at) >= today
  ).length;

  const sourceStats = trends.reduce((acc, trend) => {
    const source = trend.trend_sources.name;
    acc[source] = (acc[source] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const topSource = Object.entries(sourceStats).sort((a, b) => b[1] - a[1])[0];

  return (
    <div className="min-h-screen bg-background">
      <div className="container-responsive py-6 md:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Header */}
            <div className="space-y-4 animate-fade-in">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h1 className="bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                    Trend Browser
                  </h1>
                  <p className="text-base md:text-lg text-muted-foreground mt-2">
                    Discover the latest trends from HackerNews, Product Hunt & Reddit
                  </p>
                </div>
                
                <div className="flex items-center gap-2 md:gap-3 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={handleSyncTrends}
                    disabled={syncing}
                    className="gap-2"
                  >
                    <TrendingUp className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync Trends'}
                  </Button>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      id="top-trends"
                      checked={topTrendsMode}
                      onCheckedChange={setTopTrendsMode}
                    />
                    <Label htmlFor="top-trends" className="cursor-pointer flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Top Rated
                    </Label>
                  </div>
                </div>
              </div>

              {/* Search and Filters */}
              <Card className="p-4 shadow-sm">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search trends by keyword..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant={sourceFilter === '' ? 'default' : 'outline'}
                      onClick={() => setSourceFilter('')}
                      size="sm"
                    >
                      All
                    </Button>
                    {sources.map((source) => (
                      <Button
                        key={source}
                        variant={sourceFilter === source ? 'default' : 'outline'}
                        onClick={() => setSourceFilter(source)}
                        size="sm"
                      >
                        {source}
                      </Button>
                    ))}
                  </div>
                </div>
              </Card>
            </div>

            {/* Trends Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16 md:py-20">
                <div className="text-center">
                  <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-base text-muted-foreground">Loading trends...</p>
                </div>
              </div>
            ) : filteredTrends.length === 0 ? (
              <Card className="p-8 md:p-12 text-center shadow-sm space-y-4 animate-fade-in">
                <Sparkles className="h-12 w-12 mx-auto text-muted-foreground" />
                <div>
                  <h3 className="font-semibold text-xl mb-2">No Trends Yet</h3>
                  <p className="text-base text-muted-foreground mb-4">
                    {trends.length === 0 
                      ? 'Click "Sync Trends" to fetch the latest from HackerNews, Product Hunt & Reddit.'
                      : 'No trends match your search or filters. Try adjusting them.'}
                  </p>
                  {trends.length === 0 && (
                    <Button onClick={handleSyncTrends} disabled={syncing} className="hover-scale font-semibold">
                      <TrendingUp className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync Trends Now'}
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Showing {filteredTrends.length} trends
                </p>
                
                <div className="grid-trends">
                  {filteredTrends.map((trend) => (
                    <Card 
                      key={trend.id} 
                      className="idea-card group p-5 md:p-6 animate-fade-in"
                    >
                      <div className="space-y-4">
                        {/* Header */}
                        <div className="space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <Badge variant="secondary" className="font-medium">
                              {trend.trend_sources.name}
                            </Badge>
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatDistanceToNow(new Date(trend.occurred_at), { addSuffix: true })}
                            </span>
                          </div>
                          
                          <h3 className="text-lg font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
                            {trend.title}
                          </h3>
                        </div>

                        {/* Summary */}
                        {trend.summary && (
                          <p className="text-sm text-muted-foreground line-clamp-3">
                            {cleanText(trend.summary)}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">{trend.score}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {trend.url && (
                              <Button
                                variant="ghost"
                                size="sm"
                                asChild
                                className="h-8"
                              >
                                <a
                                  href={trend.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                </a>
                              </Button>
                            )}
                            <Button
                              size="sm"
                              onClick={() => handleGenerateIdea(trend)}
                              disabled={generatingId === trend.id}
                              className="h-8 hover-scale font-medium"
                            >
                              {generatingId === trend.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Sparkles className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Analytics Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              Analytics
            </h2>

            <Card className="p-6 shadow-sm">
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Trends Today</p>
                  <p className="text-3xl font-bold text-primary">{trendsToday}</p>
                </div>
                
                <div className="space-y-2 pt-4 border-t">
                  <p className="text-sm text-muted-foreground">Top Source</p>
                  {topSource ? (
                    <>
                      <p className="text-xl font-semibold">{topSource[0]}</p>
                      <p className="text-sm text-muted-foreground">{topSource[1]} trends</p>
                    </>
                  ) : (
                    <p className="text-sm">No data</p>
                  )}
                </div>
                
                <div className="space-y-2 pt-4 border-t">
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    Ideas Generated
                  </p>
                  <p className="text-3xl font-bold text-accent">{totalIdeasGenerated}</p>
                </div>
              </div>
            </Card>

            <Card className="p-6 shadow-sm bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="space-y-2">
                <h3 className="font-semibold">💡 Pro Tip</h3>
                <p className="text-sm text-muted-foreground">
                  Click the sparkle icon on any trend to instantly generate a startup idea based on that trend!
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* Idea Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">{previewIdea?.title}</DialogTitle>
            <DialogDescription>
              <Badge variant="secondary" className="mt-2">
                ✓ Saved to Your Ideas
              </Badge>
              <p className="mt-2">This startup idea has been generated and saved to your dashboard.</p>
            </DialogDescription>
          </DialogHeader>
          
          {previewIdea && (
            <div className="space-y-6 py-4">
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Problem</h4>
                <p className="text-sm">{previewIdea.problem}</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Solution</h4>
                <p className="text-sm">{previewIdea.solution}</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Ideal Customer Profile</h4>
                <p className="text-sm">{previewIdea.icp}</p>
              </div>
              
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Marketing Hooks</h4>
                <ul className="list-disc list-inside space-y-1">
                  {previewIdea.hooks.map((hook, i) => (
                    <li key={i} className="text-sm">{hook}</li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-sm text-muted-foreground mb-2">Launch Plan</h4>
                <ol className="list-decimal list-inside space-y-1">
                  {previewIdea.plan.map((step, i) => (
                    <li key={i} className="text-sm">{step}</li>
                  ))}
                </ol>
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Viability Score:</span>
                  <Badge variant="secondary" className="text-lg font-bold">
                    {previewIdea.score}/100
                  </Badge>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
              Close
            </Button>
            <Button onClick={handleSaveIdea}>
              <BarChart3 className="h-4 w-4 mr-2" />
              View in Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
